/* eslint-disable prefer-const */
import { Col, Form, Input, Row, Select, Spin } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUpdateCompany } from "../../../../api/company";
import { useGetInfoByUserCode } from "../../../../api/profile";
import { ReactComponent as CheckSelectIcon } from "../../../../common/images/svg/CheckSelect.svg";
import { ReactComponent as ArrowIcon } from "../../../../common/images/svg/SuffixSelectIcon.svg";
import { CustomButton } from "../../../../components/button/buttonCustom";
import { SmartUidAutoCompleteInput } from "../../../../components/inputs/autocomplete/smartUidAutocomplete";
import { ButtonOutlinedCustom } from "../../../../components/button/buttonOulined";
import { useNotification } from "../../../../components/providers/notification/useNotification";
import { companyRegistrationNumberRules } from "../../../../contants/addCompany.constants";
import {
  cityRules,
  clearFieldErrorMessageOnChange,
  companyNameRules,
  directorNameRules,
  emailRules,
  nameRules,
  onlyLatinCharsDigitsRule,
  phoneNumberRules,
  postalCodeRules,
  tinRules,
  websiteRules,
} from "../../../../utils/form.constants";
import useResponsive from "../../../../utils/hooks/useResponsive";
import { ReactComponent as PlusIcon } from "../../../../common/images/svg/Plus.svg";

const { TextArea } = Input;

interface IDetailInfoProps {
  tabActive: "info" | "bank" | "wallet";
  id: string | undefined;
  detailCompany: any;
  metadataCountries: {
    value: string;
    label: string;
  }[];
  isLoadingDetail: boolean;
  handleChangeTab: (value: string) => void;
  tabList: {
    name: string;
    value: string;
  }[];
}

const requiredFields = [
  "companyName",
  "nickName",
  "directorFirstName",
  "directorLastName",
  "address1",
  "city",
  "stateProvince",
  "companyOfficiallyRegistered",
  "tinVatUid",
  "regNo",
  "phoneNumber1",
  "email",
  "website",
];

const DetailInfo = ({
  handleChangeTab,
  tabList,
  tabActive,
  id,
  metadataCountries,
  detailCompany,
  isLoadingDetail,
}: IDetailInfoProps) => {
  const [form] = Form.useForm();
  const { isTablet } = useResponsive();
  const { open: openNotification } = useNotification();
  const navigate = useNavigate();
  const [searchCountry, setSearchCountry] = useState<string>("");
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false);
  const [infoByUserCode, setInfoByUserCode] = useState<any>(null);
  const [beneficiaryType, setBeneficiaryType] = useState("NEW");
  const [disableField, setDisableField] = useState(false);

  const { mutate: fetchInfoByUserCode, isLoading } = useGetInfoByUserCode({
    onSuccess: (response: { data: { data: any } }) => {
      setInfoByUserCode(response?.data?.data);
      form.setFields([
        {
          name: "smartUID",
          errors: undefined,
        },
      ]);
    },
    onError: (error: { message: string; reason_code: string }) => {
      setInfoByUserCode(
        error?.reason_code === "ERR00010"
          ? "This SMART UID does not exist in SMART+"
          : error?.message
      );
      if (error?.reason_code === "ERR00010") {
        form.setFields([
          {
            name: "smartUID",
            errors: ["This SMART UID does not exist in SMART+"],
          },
        ]);
      } else {
        form.setFields([
          {
            name: "smartUID",
            errors: [error?.message || ""],
          },
        ]);
      }
    },
  });

  const { mutate: updateComapny, isLoading: loadingUpdate } = useUpdateCompany({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification({
          key: "update_success",
          message:
            response.data.message ||
            "Update Beneficiary & Remitter successfully!",
          type: "success",
        });
      }
      navigate("/beneficiaries");
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: "update_failed",
          message: error.message || "Update Beneficiary & Remitter failed!",
          type: "error",
        });
      }
    },
  });

  const checkAllFieldsFilled = () => {
    const values = form.getFieldsValue(requiredFields);
    return requiredFields.every((field) => !!values[field]);
  };

  const handleChangeCountry = (value: string) => {
    setSearchCountry(value);
  };

  const onSubmitInfo = (values: any) => {
    if (loadingUpdate || !id) return;
    const body = {
      id: Number(id),
      fullName: values.companyName || "",
      name: values.nickName || "",
      directorFirstName: values.directorFirstName || "",
      directorLastName: values.directorLastName || "",
      address1: values.address1 || "",
      address2: values.address2 || "",
      city: values.city || "",
      stateProvince: values.stateProvince || "",
      country: values.companyOfficiallyRegistered || "",
      tinVatUid: values.tinVatUid || "",
      regNo: values.regNo || "",
      phoneNumber1: values.phoneNumber1 || "",
      email: values.email || "",
      smartUID: values.smartUID ? String(values.smartUID).trim() : null,
      website: values.website || "",
      postalCode: values?.postalCode || "",
      refUserId: infoByUserCode?.id ? infoByUserCode?.id : undefined,
    };
    updateComapny(body);
  };

  useEffect(() => {
    if (!detailCompany) return;
    if (tabActive !== "info") form.resetFields();
    else {
      form.setFieldsValue({
        companyName:
          detailCompany?.refUser?.fullName || detailCompany?.fullName || "",
        nickName: detailCompany?.name || "",
        directorFirstName: detailCompany?.directorFirstName || "",
        directorLastName: detailCompany?.directorLastName || "",
        address1:
          detailCompany?.refUser?.address || detailCompany?.address1 || "",
        address2: detailCompany?.address2 || "",
        city: detailCompany?.city || "",
        stateProvince: detailCompany?.stateProvince || "",
        companyOfficiallyRegistered:
          detailCompany?.refUser?.country || detailCompany?.country || "",
        tinVatUid: detailCompany?.tinVatUid || "",
        regNo: detailCompany?.refUser?.regNo || detailCompany?.regNo || "",
        phoneNumber1: detailCompany?.phoneNumber1 || "",
        email: detailCompany?.refUser?.email || detailCompany?.email || "",
        smartUID: detailCompany?.refUser?.smartUID || "",
        website:
          detailCompany?.refUser?.website || detailCompany?.website || "",
        postalCode: detailCompany?.postalCode || "",
      });
      setSearchCountry(
        detailCompany?.refUser?.country || detailCompany?.country
      );
      if (detailCompany?.refUser?.smartUID) {
        setBeneficiaryType("FIND_ON_SMART");
        fetchInfoByUserCode(detailCompany?.refUser?.smartUID);
      }
    }
  }, [detailCompany, tabActive]);

  useEffect(() => {
    if (form.getFieldValue("smartUID")?.trim() === detailCompany?.smartUID) {
      form.setFieldsValue({
        companyName:
          detailCompany?.refUser?.fullName || detailCompany?.fullName || "",
        address1:
          detailCompany?.refUser?.address || detailCompany?.address1 || "",
        companyOfficiallyRegistered:
          detailCompany?.refUser?.country || detailCompany?.country || "",
        regNo: detailCompany?.refUser?.regNo || detailCompany?.regNo || "",
        email: detailCompany?.refUser?.email || detailCompany?.email || "",
        website:
          detailCompany?.refUser?.website || detailCompany?.website || "",
      });
      return;
    }
    if (!infoByUserCode || typeof infoByUserCode === "string") {
      setDisableField(false);
      return;
    }
    setDisableField(true);
    form.setFieldsValue({
      companyName: infoByUserCode?.fullName,
      address1: infoByUserCode?.address,
      companyOfficiallyRegistered: infoByUserCode?.country,
      regNo: infoByUserCode?.regNo,
      email: infoByUserCode?.email,
      website: infoByUserCode?.website,
    });
  }, [infoByUserCode]);

  const resetFieldsAfterSmartUidChange = () => {
    form?.resetFields([
      "companyName",
      "nickName",
      "directorFirstName",
      "directorLastName",
      "address1",
      "address2",
      "city",
      "stateProvince",
      "companyOfficiallyRegistered",
      "tinVatUid",
      "regNo",
      "phoneNumber1",
      "email",
      "website",
      "postalCode",
    ]);
  };

  const fetchProfileBySmartUid = async (rawCode: string) => {
    const code = rawCode?.trim() ?? "";
    resetFieldsAfterSmartUidChange();
    await fetchInfoByUserCode(code);
  };

  const handleBlur = async () => {
    const code = form.getFieldValue("smartUID")
      ? form.getFieldValue("smartUID")?.trim()
      : "";

    if (!code) {
      setDisableField(false);
      resetFieldsAfterSmartUidChange();
      form.setFields([
        {
          name: "smartUID",
          errors: undefined,
        },
      ]);
      setInfoByUserCode(null);
      return;
    }
    await fetchProfileBySmartUid(code);
  };

  return (
    <div
      className="detail-info-content"
      style={{ display: tabActive === "info" ? "flex" : "none" }}
    >
      {isLoadingDetail ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            minHeight: "400px",
          }}
        >
          <Spin size="large" className="custom-spin" />
        </div>
      ) : (
        <Form
          style={{ maxWidth: "1020px" }}
          form={form}
          onFinish={onSubmitInfo}
          onValuesChange={() => {
            clearFieldErrorMessageOnChange(form);
            setIsAllFieldsFilled(checkAllFieldsFilled());
          }}
        >

          {tabList?.length > 0 ? (
            <div className="tab-list-info">
              {tabList.map((item, index) => (
                <CustomButton
                  key={index}
                  size={isTablet ? "small" : "medium"}
                  text={item.name}
                  icon={<PlusIcon />}
                  onClick={() => handleChangeTab(item.value)}
                />
              ))}
            </div>
          ) : <></>}

          {beneficiaryType === "FIND_ON_SMART" ? (
            <div
              className="form-info-content"
              style={{
                paddingBottom: "0px",
                paddingTop: "0px",
              }}
            >
              <Col
                style={{
                  marginTop: "12px",
                  paddingBottom: "24px",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
                }}
                xs={24}
              >
                <Form.Item
                  label="SMART UID"
                  validateTrigger="onBlur"
                  name="smartUID"
                  rules={[
                    {
                      required: true,
                      message: "This field is required",
                    },
                    {
                      max: 256,
                      message: "Maximum 256 characters",
                      validateTrigger: "onBlur",
                    },
                  ]}
                >
                  <SmartUidAutoCompleteInput
                    placeholder="Enter the code linked to your SMART account (min. 3 characters)"
                    onBlur={handleBlur}
                    onSuggestionSelect={(uid) => fetchProfileBySmartUid(uid)}
                  />
                </Form.Item>
              </Col>
            </div>
          ) : null}

          <div className="form-info-content">
            <Row gutter={{ xs: 8, sm: 30, md: isTablet ? 40 : 60 }}>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Name"
                  name="companyName"
                  rules={companyNameRules}
                  validateTrigger="onBlur"
                >
                  <Input
                    disabled={disableField}
                    placeholder="Enter the full legal name of the company"
                  />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Nickname"
                  name="nickName"
                  validateTrigger="onBlur"
                  rules={[...nameRules, onlyLatinCharsDigitsRule]}
                >
                  <Input placeholder="Enter a short name or alias for internal use" />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Reg No"
                  name="regNo"
                  validateTrigger="onBlur"
                  rules={companyRegistrationNumberRules}
                >
                  <Input
                    disabled={disableField}
                    placeholder="Enter the official company registration number"
                  />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="TIN"
                  name="tinVatUid"
                  validateTrigger="onBlur"
                  rules={tinRules}
                >
                  <Input placeholder="Enter the company’s Tax Identification Number" />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Address 1"
                  name="address1"
                  validateTrigger="onBlur"
                  rules={[
                    {
                      required: true,
                      message: "This field is required",
                      validateTrigger: "onBlur",
                      whitespace: true,
                    },
                    {
                      max: 256,
                      message: "Maximum 256 characters",
                      validateTrigger: "onBlur",
                    },
                  ]}
                >
                  <TextArea
                    disabled={disableField}
                    className="beneficiary-address"
                    autoSize={{ minRows: 1, maxRows: 5 }}
                    placeholder="Enter the primary street address of the company"
                  />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Address 2"
                  name="address2"
                  validateTrigger="onBlur"
                  rules={[
                    {
                      max: 256,
                      message: "Maximum 256 characters",
                      validateTrigger: "onBlur",
                    },
                  ]}
                >
                  <TextArea
                    className="beneficiary-address"
                    autoSize={{ minRows: 1, maxRows: 5 }}
                    placeholder="Additional address details (e.g., building, floor)"
                  />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="City"
                  name="city"
                  validateTrigger="onBlur"
                  rules={cityRules}
                >
                  <Input placeholder="Enter the city where the company is located" />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="State/Province"
                  name="stateProvince"
                  validateTrigger="onBlur"
                  rules={cityRules}
                >
                  <Input placeholder="Enter the state or province of company location" />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Country of incorporation"
                  name="companyOfficiallyRegistered"
                  validateTrigger="onBlur"
                  rules={[
                    {
                      required: true,
                      message: "This field is required",
                    },
                  ]}
                >
                  <Select
                    disabled={disableField}
                    showSearch
                    placeholder="Select the country where the company is officially registered"
                    suffixIcon={<ArrowIcon />}
                    onChange={handleChangeCountry}
                    optionLabelProp="label"
                    className="select-country-beneficiary"
                    filterOption={(input, option) => {
                      const label = option?.label;
                      if (typeof label === "string") {
                        return label
                          .toLowerCase()
                          .includes(input.toLowerCase());
                      }
                      return false;
                    }}
                  >
                    {metadataCountries.map((item) => (
                      <Select.Option
                        key={item.value}
                        value={item.value}
                        label={item.label}
                      >
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 2,
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                            }}
                          >
                            {item.label}
                          </span>
                          {searchCountry === item.value && (
                            <CheckSelectIcon style={{ flexShrink: 0 }} />
                          )}
                        </span>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Postal Code"
                  name="postalCode"
                  validateTrigger="onBlur"
                  rules={postalCodeRules}
                >
                  <Input placeholder="Enter your postal code" />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Director's First Name"
                  name="directorFirstName"
                  validateTrigger="onBlur"
                  rules={directorNameRules}
                >
                  <Input placeholder="Enter the first name(s) of the company director" />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Director's Last Name"
                  name="directorLastName"
                  validateTrigger="onBlur"
                  rules={directorNameRules}
                >
                  <Input placeholder="Enter the last name (surname) of the company director" />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Tel"
                  name="phoneNumber1"
                  validateTrigger="onBlur"
                  rules={phoneNumberRules}
                >
                  <Input placeholder="Enter the official company phone number" />
                </Form.Item>
              </Col>
              <Col
                style={{ marginBottom: isTablet ? "18px" : "24px" }}
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Email"
                  name="email"
                  validateTrigger="onBlur"
                  rules={[
                    ...emailRules,
                    {
                      required: true,
                      message: "This field is required",
                      validateTrigger: "onBlur",
                    },
                  ]}
                >
                  <Input
                    disabled={disableField}
                    placeholder="Enter the official company email address"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Website"
                  name="website"
                  validateTrigger="onBlur"
                  rules={websiteRules}
                >
                  <Input
                    disabled={disableField}
                    placeholder="Enter the company's official website URL"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <div className="form-edit-info-footer">
            <ButtonOutlinedCustom
              text={"Cancel"}
              size={isTablet ? "medium" : "large"}
              width={136}
              onClick={() => navigate("/beneficiaries")}
              isLoading={loadingUpdate}
            />
            <CustomButton
              type="submit"
              text={"Save"}
              size={isTablet ? "medium" : "large"}
              width={192}
              isLoading={loadingUpdate}
              disabled={
                !isAllFieldsFilled ||
                isLoading ||
                form.getFieldError("smartUID")?.length > 0 ||
                (beneficiaryType === "FIND_ON_SMART" && !infoByUserCode)
              }
            />
          </div>
        </Form>
      )}
    </div>
  );
};

export default DetailInfo;
